import { useEffect } from "react"
import { useInRouterContext, useSearchParams } from "react-router-dom"

/**
 * Bring the row a deep link names into view, and mark it.
 *
 * "Review milestone" has to open *that* milestone, not the list it sits in.
 * The row is found by the `data-row-key` AntD already writes from `rowKey`,
 * so no table needs to know a link exists; the observer is what covers the
 * row arriving after its page has loaded.
 *
 * The parameter is cleared once honoured, so a refresh or a back-navigation
 * does not re-scroll a list the reader has since moved around in — and a row
 * that never arrives (on a later page, or since deleted) simply leaves the
 * reader on the right tab rather than on an error.
 *
 * It does nothing outside a Router. This is an enhancement over a list that
 * worked without one: making a section that never needed a Router suddenly
 * throw without it would be a worse bug than the deep link is a feature, and
 * with no URL there is nothing to read a parameter from anyway.
 */
export const FOCUS_ROW_CLASS = "focus-target-row"

const WAIT_MS = 8000
const MARK_MS = 2600

export function useFocusTableRow(param: string, ready = true) {
  // A conditional hook call, deliberately: a mounted component never moves
  // in or out of a Router, so this branch is fixed for its whole lifetime
  // and the hook order cannot change between renders. The alternative is
  // calling `useSearchParams` unconditionally, which throws outright
  // without a Router — and the sections this runs in render without one in
  // their own specs, and worked without one before this was added.
  return useInRouterContext() ? useRoutedFocusTableRow(param, ready) : undefined
}

function useRoutedFocusTableRow(param: string, ready: boolean) {
  const [searchParams, setSearchParams] = useSearchParams()
  const wanted = searchParams.get(param)

  useEffect(() => {
    if (!wanted || !ready) return

    let marked: Element | null = null
    let settled = false

    const mark = (row: Element) => {
      settled = true
      marked = row
      row.classList.add(FOCUS_ROW_CLASS)
      row.scrollIntoView({ behavior: "smooth", block: "center" })
      // The row is announced rather than focused: moving focus into a table
      // body would strand a keyboard reader inside it.
      row.setAttribute("aria-current", "true")
      window.setTimeout(() => {
        row.classList.remove(FOCUS_ROW_CLASS)
        row.removeAttribute("aria-current")
      }, MARK_MS)
      clear()
    }

    const clear = () => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          next.delete(param)
          return next
        },
        { replace: true }
      )
    }

    const find = () =>
      document.querySelector(`[data-row-key="${CSS.escape(wanted)}"]`)

    const existing = find()
    if (existing) {
      mark(existing)
      return
    }

    const observer = new MutationObserver(() => {
      const row = find()
      if (row) {
        observer.disconnect()
        mark(row)
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })

    const giveUp = window.setTimeout(() => {
      observer.disconnect()
      if (!settled) clear()
    }, WAIT_MS)

    return () => {
      observer.disconnect()
      window.clearTimeout(giveUp)
      if (marked) {
        marked.classList.remove(FOCUS_ROW_CLASS)
        marked.removeAttribute("aria-current")
      }
    }
  }, [wanted, ready, param, setSearchParams])
}
